import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import { deleteProposalWithDependencies } from "components/Modal/confModalTypes";
import { modalTypes } from "constants/types";
import useShowArchiveEntityModal from "hooks/useShowArchiveEntityModal";
import { Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { bool } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  deleteProposal,
  fetchProposalData,
  showDeleteProposalBanner,
} from "slices/proposals/actions";
import {
  archiveProposal,
  unarchiveProposal,
} from "slices/proposals/list/actions";
import { selectProposal } from "slices/proposals/proposal/selectors";

import classes from "./Options.module.scss";

const Options = ({ isArchived }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showArchiveEntityModal = useShowArchiveEntityModal();
  const proposal = useSelector(selectProposal);

  const onDelete = async () => {
    await dispatch(deleteProposal(proposal.id));
    navigate(isArchived ? "/proposals/archived" : "/proposals/kanban");
    dispatch(showDeleteProposalBanner());
    dispatch(hideModal(modalTypes.CONFIRMATION));
  };

  const handleDelete = async () => {
    const {
      localDocumentCount,
      userCreatedDocs,
      amendmentReportCount,
      shredCount,
    } = await dispatch(fetchProposalData(proposal.id));
    const proposalTitle = proposal.title;

    const hasDependencies =
      localDocumentCount > 0 ||
      userCreatedDocs > 0 ||
      amendmentReportCount > 0 ||
      shredCount > 0;

    if (hasDependencies) {
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          deleteProposalWithDependencies(
            dispatch,
            onDelete,
            localDocumentCount,
            userCreatedDocs,
            amendmentReportCount,
            shredCount,
            proposalTitle
          )
        )
      );
    } else {
      await onDelete();
    }
  };

  const handleArchive = async () => {
    if (isArchived) {
      if (proposal.hasArchivedOpportunity) {
        dispatch(
          showModal(modalTypes.UNARCHIVE_PROPOSAL, {
            proposalId: proposal.id,
            proposalTitle: proposal.title,
          })
        );
      } else {
        await dispatch(unarchiveProposal(proposal.id, false));
        navigate("/proposals/archived");
      }
      return;
    }

    showArchiveEntityModal({
      primarySlot: { entityType: "proposals", label: "Proposal" },
      entityName: proposal.title,
      ...(!proposal.hasArchivedOpportunity && {
        optionalSlot: {
          entityType: "tracked-opportunities",
          label: "Tracked Opportunity",
        },
      }),
      onConfirm: async ({ statusIdsBySlot, note }) => {
        await dispatch(
          archiveProposal(proposal.id, {
            includeOpportunity: statusIdsBySlot.optional !== undefined,
            archiveStatusId: statusIdsBySlot.primary,
            opportunityArchiveStatusId: statusIdsBySlot.optional,
            archiveNote: note,
          })
        );
        navigate("/proposals/dashboard");
      },
    });
  };

  return (
    <div className={classes.options}>
      {isArchived ? (
        <NewButton
          variant={NewButtonVariant.l1_5}
          rightIcon={<ArchiveRestore size={14} />}
          onClick={handleArchive}
        >
          Unarchive
        </NewButton>
      ) : (
        <NewButton
          variant={NewButtonVariant.l2}
          rightIcon={<Archive size={14} />}
          onClick={handleArchive}
        >
          Archive
        </NewButton>
      )}

      <NewButton
        variant={NewButtonVariant.red}
        title="Delete Proposal"
        classes={classes.deleteButton}
        onClick={handleDelete}
        leftIcon={<Trash2 size={16} />}
      />
    </div>
  );
};

export default Options;

Options.propTypes = {
  isArchived: bool,
};
